<template>
  <div class="pedido-listado">
    <section class="pedido-steps">
      <div class="container">
        <div class="return d-block d-sm-none">
          <router-link :to="route">
            <img :src="require('../../../assets/img/backspace.svg')" class="mb-5" />
          </router-link>
        </div>

        <div class="section-title">
          <h5 class="pull-left">Importador</h5>
          <router-link :to="route">
            <img class="d-none d-sm-block" :src="require('../../../assets/img/backspace.svg')" />
          </router-link>
        </div>

        <form class="form-filter">
          <div class="row">
            <div class="col-md-6">
              <div class="form-group">
                <label for="table-select">Seleccione la tabla:</label>
                <select v-model="selectedTable" id="table-select" class="form-control" :disabled="isUploading">
                  <option disabled value="">-- Seleccione una tabla --</option>
                  <option value="pricing_sold_to">Pricing Sold To</option>
                  <option value="plant_sku">Plant SKU</option>
                  <option value="freight_mapping">Freight Mapping</option>
                  <option value="precio_competencia">Precio Competencia</option>
                  <option value="profit">Profit</option>
                </select>
              </div>
            </div>
          </div>

          <div class="row mt-2">
            <div class="col-md-12 next-btn">
              <a @click="downloadTemplate" 
                 class="btn-green" 
                 :class="{ disabled: !selectedTable || isUploading }" 
                 href="javascript:void(0)">
                Descargar Template
              </a>
              <a @click="triggerUpload" 
                 class="btn-green" 
                 :class="{ disabled: !selectedTable || isUploading }" 
                 href="javascript:void(0)">
                {{ isUploading ? 'Procesando...' : 'Importar' }}
              </a>
            </div>
          </div>
        </form>

        <!-- Indicador de procesamiento -->
        <div v-if="isUploading" class="text-center mt-4">
          <div class="spinner-border text-primary" role="status">
            <span class="sr-only">Procesando...</span>
          </div>
          <p class="mt-2 text-muted">
            Procesando archivo... Por favor espere.
            <br>
            <small>Esto puede tomar algunos minutos para archivos grandes.</small>
          </p>
        </div>

        <input ref="uploadInput" 
               type="file" 
               accept=".xlsx,.xls" 
               style="display: none;" 
               @change="handleUpload" />

        <div v-if="importHistory.length === 0 && !isLoading" class="text-center text-gray-500 py-6">
          Aún no hay registros de importaciones.
        </div>
        
        <div v-if="isLoading" class="text-center py-6">
          <div class="spinner-border" role="status">
            <span class="sr-only">Cargando...</span>
          </div>
        </div>

        <div v-else-if="importHistory.length > 0" class="history-section mt-4">
          <h3>Histórico de Importaciones</h3>
          
          <table class="bg-white d-none d-sm-table">
            <thead>
              <tr style="background-color: #fffadc;">
                <th class="text-center">Nombre del archivo</th>
                <th class="text-center">Tabla</th>
                <th class="text-center">Fecha de importación</th>
                <th class="text-center">Líneas importadas</th>
              </tr>
            </thead>
            <tbody>
              <tr 
                v-for="(history, index) in paginatedHistory" 
                :key="index"
                @click="downloadImportedFile(history.filename)"
                style="cursor: pointer;"
                title="Clique para descargar el archivo"
              >
                <td class="text-center">{{ history.filename }}</td>
                <td class="text-center">{{ getTableDisplayName(history.table) }}</td>
                <td class="text-center">{{ formatDate(history.date) }}</td>
                <td class="text-center">
                  <span :class="getLinesBadgeClass(history.lines)">
                    {{ formatNumber(history.lines) }}
                  </span>
                </td>
              </tr>
            </tbody>
          </table>

          <table class="bg-white d-block d-sm-none importHistoryTableMobile b-top"
            v-for="(history, index) in paginatedHistory" :key="index">
            <tr><th>Nombre del archivo</th><td>{{ history.filename }}</td></tr>
            <tr><th>Tabla</th><td>{{ getTableDisplayName(history.table) }}</td></tr>
            <tr><th>Fecha de importación</th><td>{{ formatDate(history.date) }}</td></tr>
            <tr>
              <th>Líneas importadas</th>
              <td>
                <span :class="getLinesBadgeClass(history.lines)">
                  {{ formatNumber(history.lines) }}
                </span>
              </td>
            </tr>
          </table>

          <nav v-if="totalPages > 1">
            <ul class="pagination mt-3">
              <li class="page-item" :class="{ disabled: currentPage === 1 }">
                <a class="page-link" href="#" @click.prevent="changePage(currentPage - 1)">‹</a>
              </li>
              <li class="page-item" 
                  v-for="pageNumber in visiblePageNumbers" 
                  :key="pageNumber" 
                  :class="{ active: pageNumber === currentPage }">
                <a class="page-link" href="#" @click.prevent="changePage(pageNumber)">{{ pageNumber }}</a>
              </li>
              <li class="page-item" :class="{ disabled: currentPage === totalPages }">
                <a class="page-link" href="#" @click.prevent="changePage(currentPage + 1)">›</a>
              </li>
            </ul>
          </nav>
        </div>
      </div>
    </section>
    <vue-basic-alert :duration="300" :closeIn="1000000" ref="alert" />
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import axios from 'axios'

const route = ref('/')
const selectedTable = ref('')
const uploadInput = ref(null)
const alert = ref(null)
const importHistory = ref([])
const currentPage = ref(1)
const itemsPerPage = 10
const isUploading = ref(false)
const isLoading = ref(false)

const templateLinks = {
  pricing_sold_to: '/assets/templates/Template_pricing_sold_to.xlsx',
  plant_sku: '/assets/templates/Template_plant_sku.xlsx',
  freight_mapping: '/assets/templates/Template_freight_mapping.xlsx',
  precio_competencia: '/assets/templates/Template_precio_competencia.xlsx',
  profit: '/assets/templates/Template_profit.xlsx',
}

const tableDisplayNames = {
  pricing_sold_to: 'Pricing Sold To',
  plant_sku: 'Plant SKU',
  freight_mapping: 'Freight Mapping',
  precio_competencia: 'Precio Competencia',
  profit: 'Profit'
}

const paginatedHistory = computed(() => {
  if (!Array.isArray(importHistory.value)) return []
  const startIndex = (currentPage.value - 1) * itemsPerPage
  const endIndex = startIndex + itemsPerPage
  return importHistory.value.slice(startIndex, endIndex)
})

const totalPages = computed(() => {
  return Math.ceil(importHistory.value.length / itemsPerPage)
})

const visiblePageNumbers = computed(() => {
  const maxVisiblePages = 5
  const total = totalPages.value
  let startPage = currentPage.value - Math.floor(maxVisiblePages / 2)
  startPage = Math.max(startPage, 1)
  let endPage = startPage + maxVisiblePages - 1
  endPage = Math.min(endPage, total)
  
  if (endPage - startPage + 1 < maxVisiblePages) {
    startPage = Math.max(1, endPage - maxVisiblePages + 1)
  }
  
  return Array(endPage - startPage + 1)
    .fill()
    .map((_, index) => startPage + index)
})

function formatNumber(value) {
  if (!value) return '0'
  
  // Usa o formato espanhol: 1.234.567
  return new Intl.NumberFormat('es-ES', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(value)
}

function getLinesBadgeClass(lines) {
  if (lines >= 1000) return 'lines-badge lines-badge-success'
  if (lines >= 500) return 'lines-badge lines-badge-primary'
  if (lines >= 100) return 'lines-badge lines-badge-info'
  return 'lines-badge lines-badge-secondary'
}

function getTableDisplayName(table) {
  return tableDisplayNames[table] || table
}

function formatDate(dateString) {
  const date = new Date(dateString)
  return date.toLocaleString('es-ES', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit'
  })
}

function showAlert(type, title, message) {
  if (alert.value) {
    alert.value.showAlert(
      type,
      message,
      title,
      {
        iconSize: 35,
        iconType: 'solid',
        position: 'top right'
      }
    )
  }
}

function triggerUpload() {
  if (!selectedTable.value) {
    showAlert('warning', 'Aviso', 'Seleccione una tabla antes de hacer upload.')
    return
  }

  if (isUploading.value) {
    showAlert('warning', 'Aviso', 'Ya hay una importación en progreso.')
    return
  }

  if (uploadInput.value) {
    uploadInput.value.value = ''
  }

  uploadInput.value.click()
}

function downloadTemplate() {
  const selectedKey = selectedTable.value
  const link = templateLinks[selectedKey]

  if (!link) {
    showAlert('warning', 'Aviso', 'Seleccione una tabla para descargar el template.')
    return
  }

  const fileName = link.split('/').pop()
  const a = document.createElement('a')
  a.href = link
  a.setAttribute('download', fileName)
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
}

async function handleUpload(event) {
  const file = event.target.files[0]
  if (!file) return

  const maxSize = 20 * 1024 * 1024 // 20MB
  if (file.size > maxSize) {
    showAlert('error', 'Error', 'El archivo excede el tamaño máximo permitido de 20MB.')
    event.target.value = ''
    return
  }

  isUploading.value = true

  const formData = new FormData()
  formData.append('file', file)
  formData.append('table', selectedTable.value)

  try {
    const response = await axios.post('pricing/uploadGeneric', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 300000, // 5 minutos
    })

    if (response.data?.error === true) {
      if (response.data?.errors) {
        const errorMessages = response.data.errors
          .slice(0, 10) // Mostrar máximo 10 errores
          .map((error) => `Fila ${error.row}: ${error.message}`)
        
        const moreErrors = response.data.errors.length > 10 
          ? `\n... y ${response.data.errors.length - 10} errores más.` 
          : ''
        
        showAlert(
          'error',
          'Error de validación',
          'El archivo Excel contiene errores de validación:\n' +
            errorMessages.join('\n') + moreErrors
        )
      } else {
        showAlert(
          'error',
          'Error',
          response.data.message || 'Error al enviar el archivo.'
        )
      }
      return
    }

    showAlert(
      'success',
      'Éxito',
      response.data.message || 'Archivo importado con éxito.'
    )

    event.target.value = ''
    
    fetchImportHistory()
  } catch (error) {
    console.error('Error al enviar archivo:', error)
    
    if (error.code === 'ECONNABORTED') {
      showAlert(
        'error',
        'Error',
        'La importación excedió el tiempo límite. Por favor, intente con un archivo más pequeño.'
      )
    } else if (error.response) {
      showAlert(
        'error',
        'Error',
        error.response.data?.message || 'Error en la respuesta del servidor.'
      )
    } else if (error.request) {
      showAlert('error', 'Error', 'No se recibió respuesta del servidor.')
    } else {
      showAlert('error', 'Error', 'Error al enviar la solicitud.')
    }
  } finally {
    isUploading.value = false
  }
}

function changePage(newPage) {
  if (newPage >= 1 && newPage <= totalPages.value) {
    currentPage.value = newPage
  }
}

function fetchImportHistory() {
  isLoading.value = true
  
  axios.get('pricing/imports')
    .then(response => {
      importHistory.value = Array.isArray(response.data.imports) 
        ? response.data.imports 
        : []
      
      if (currentPage.value > totalPages.value) {
        currentPage.value = 1
      }
    })
    .catch(error => {
      console.error('Error al obtener el histórico de importaciones:', error)
      importHistory.value = []
    })
    .finally(() => {
      isLoading.value = false
    })
}

async function downloadImportedFile(filename) {
  if (!filename) return

  try {
    const response = await axios.get(`pricing/download/${encodeURIComponent(filename)}`, {
      responseType: 'blob',
    })

    const blob = new Blob([response.data])
    const url = window.URL.createObjectURL(blob)

    const a = document.createElement('a')
    a.href = url
    a.download = filename
    document.body.appendChild(a)
    a.click()
    a.remove()
    window.URL.revokeObjectURL(url)

  } catch (error) {
    console.error('Error al descargar el archivo:', error)
    showAlert('error', 'Error', 'Ocurrió un error al intentar descargar el archivo.')
  }
}

onMounted(() => {
  fetchImportHistory()
})
</script>

<style scoped>
.sub-header {
  margin-top: 20px;
  margin-bottom: 20px;
}

.spinner-border {
  width: 3rem;
  height: 3rem;
}

.importHistoryTableMobile {
  margin-bottom: 15px;
}

.disabled {
  opacity: 0.6;
  cursor: not-allowed !important;
}

.lines-badge {
  display: inline-block;
  padding: 0.35em 0.65em;
  font-size: 0.875em;
  font-weight: 700;
  line-height: 1;
  text-align: center;
  white-space: nowrap;
  vertical-align: baseline;
  border-radius: 0.375rem;
  transition: all 0.2s ease-in-out;
}

.lines-badge-success {
  background-color: #d4edda;
  color: #155724;
  border: 1px solid #c3e6cb;
}

.lines-badge-primary {
  background-color: #d1ecf1;
  color: #0c5460;
  border: 1px solid #bee5eb;
}

.lines-badge-info {
  background-color: #d6e9f9;
  color: #004085;
  border: 1px solid #b8daff;
}

.lines-badge-secondary {
  background-color: #e2e3e5;
  color: #383d41;
  border: 1px solid #d6d8db;
}

tbody tr:hover {
  background-color: #f5f5f5;
  transition: background-color 0.2s ease;
}

.spinner-border {
  animation: spinner-border 0.75s linear infinite;
}

@keyframes spinner-border {
  to { transform: rotate(360deg); }
}
</style>